/**
 * `DiagramCanvas` — the React Flow canvas for a `DiagramModel` (interactive-diagrams v0.2,
 * "Lucid-basic" editor).
 *
 * Two modes:
 *  - **read-only** (`readOnly`, no `onCommit`): a clean static render at sidecar/auto-layout
 *    positions — no toolbar, no minimap.
 *  - **editable** (`onCommit` provided): the full canvas editor —
 *      · shape palette (click to add a tile at the viewport center),
 *      · drag to move (multi-select box included), drag handle-to-handle to connect,
 *      · double-click to rename, Delete to remove, drag an edge end to reconnect,
 *      · NodeResizer (persists to the sidecar `size` map),
 *      · per-node color/text styling — persisted as **native mermaid `style` directives**
 *        (portable: renders identically in any external mermaid renderer),
 *      · edge connector/arrow/label editing,
 *      · minimap + dotted grid + snap-to-grid + re-layout.
 *
 * Every mutation is serialized (minimal-diff) and handed to `onCommit`; the Tiptap NodeView
 * persists it through the editor history, so **Ctrl+Z undoes any canvas edit**.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  ControlButton,
  MiniMap,
  Panel,
  MarkerType,
  ConnectionMode,
  SelectionMode,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type Edge,
  type EdgeMarker,
  type Connection,
  type Node as RFNode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './diagram-canvas.css';
import { Position } from '@xyflow/react';
import type {
  DiagramModel,
  DiagramEdge,
  NodeShape,
  ConnectorStyle,
  Point,
  SidecarData,
  StickyNote,
  Swimlane,
} from '../model';
import { emptySidecar, effectiveNodeStyle } from '../model';
import {
  applySidecar,
  serialize,
  addNode,
  addEdge,
  removeNode,
  removeEdge,
  reconnectEdge,
  updateNodeLabel,
  updateEdge,
  setNodeStyle,
} from '../serialize';
import { mergeSidecar, reconcileAgainstModel, cloneSidecar, cloneNote, cloneLane } from '../sidecar';
import { computeLayout, estimateNodeSize } from '../layout';
import { elkLayout } from '../elkLayout';
import { MermaidNode, type MermaidFlowNode } from './MermaidNode';
import {
  StickyNode,
  LaneNode,
  SubgraphNode,
  STICKY_DEFAULT,
  LANE_DEFAULT,
  STICKY_DEFAULT_SIZE,
  LANE_DEFAULT_SIZE,
  type StickyNodeData,
  type LaneNodeData,
  type StickyFlowNode,
  type LaneFlowNode,
  type SubgraphFlowNode,
} from './annotations';
import { FloatingEdge } from './FloatingEdge';
import { SHAPE_MENU, ShapeGlyph } from './shapes';

const NODE_TYPES = { mermaid: MermaidNode, sticky: StickyNode, lane: LaneNode, subgraph: SubgraphNode };
const EDGE_TYPES = { floating: FloatingEdge };
/** Any node the canvas renders: a graph tile, a sticky note, a swimlane frame, or a subgraph frame. */
type AnyFlowNode = MermaidFlowNode | StickyFlowNode | LaneFlowNode | SubgraphFlowNode;
const SNAP: [number, number] = [8, 8];

/** Round a React Flow position to an integer `Point`. */
const rndPoint = (p: { x: number; y: number }): Point => [Math.round(p.x), Math.round(p.y)];
/** A node's live measured size, falling back to the given default. */
const measuredSize = (n: AnyFlowNode, fb: Point): Point => [
  Math.round(n.measured?.width ?? fb[0]),
  Math.round(n.measured?.height ?? fb[1]),
];
/** A sparse sidecar patch carrying only the annotation arrays (notes + lanes). */
const annPatch = (notes: StickyNote[], lanes: Swimlane[]): SidecarData => {
  const p = emptySidecar();
  p.notes = notes;
  p.lanes = lanes;
  return p;
};
/** The smallest `<prefix><k>` id not already taken among `existing`. */
function nextAnnId(prefix: string, existing: string[]): string {
  const set = new Set(existing);
  for (let k = 1; ; k += 1) {
    const id = `${prefix}${k}`;
    if (!set.has(id)) return id;
  }
}

/** The quick-style palette. Fill is the hue at ~13% alpha, stroke is the hue — Lucid-style
 *  container chips. Concrete hex (not theme vars) so the mermaid `style` lines stay portable. */
const PALETTE = [
  { name: 'slate', hex: '#64748b' },
  { name: 'blue', hex: '#3b82f6' },
  { name: 'teal', hex: '#14b8a6' },
  { name: 'green', hex: '#22c55e' },
  { name: 'amber', hex: '#f59e0b' },
  { name: 'orange', hex: '#f97316' },
  { name: 'red', hex: '#ef4444' },
  { name: 'violet', hex: '#8b5cf6' },
  { name: 'pink', hex: '#ec4899' },
] as const;

/** Flat lookup of shape → label (from the grouped menu) for new-tile default labels. */
const SHAPE_LABELS: Record<string, string> = Object.fromEntries(
  SHAPE_MENU.flatMap((g) => g.items.map((i) => [i.shape, i.label])),
);

const FONT_SIZES = [
  { label: 'Aa auto', value: '' },
  { label: 'Aa 11', value: '11px' },
  { label: 'Aa 13', value: '13px' },
  { label: 'Aa 15', value: '15px' },
  { label: 'Aa 18', value: '18px' },
];

export interface DiagramCanvasProps {
  model: DiagramModel;
  /** Read-only render (default). Ignored when `onCommit` is provided. */
  readOnly?: boolean;
  /** When present, the canvas is editable: called with the new mermaid source after any change. */
  onCommit?: (nextSource: string) => void;
  /** Fill the parent's height instead of the clamped inline height — used in the fullscreen overlay. */
  fill?: boolean;
  /** When present, a maximize button is shown in the bottom-left controls (opens fullscreen). */
  onMaximize?: () => void;
  /** Presentation mode: hide all chrome (toolbars, selection bar, controls, minimap, grid). */
  hideChrome?: boolean;
}

/** Clamp a diagram's rendered height to a sane inline range based on its vertical extent. */
function frameHeight(ys: number[]): number {
  if (ys.length === 0) return 280;
  const extent = Math.max(...ys) - Math.min(...ys);
  return Math.min(600, Math.max(280, extent + 200));
}

/** Dashed/thick stroke styling per connector style. */
function edgeStyle(e: DiagramEdge): React.CSSProperties {
  const style: React.CSSProperties = {};
  if (e.connector === 'dotted') style.strokeDasharray = '4 4';
  if (e.connector === 'thick') style.strokeWidth = 3;
  return style;
}

/** Arrowhead marker for an edge's terminal, or undefined for open links. */
function edgeMarker(e: DiagramEdge): EdgeMarker | undefined {
  if (e.arrow === 'open') return undefined;
  return { type: MarkerType.ArrowClosed, width: 18, height: 18 };
}

/** The smallest `n<k>` id not already taken — new-tile ids stay short and mermaid-safe. */
function nextNodeId(model: DiagramModel): string {
  for (let k = 1; ; k += 1) {
    const id = `n${k}`;
    if (!model.nodes.has(id)) return id;
  }
}

interface BuildOpts {
  interactive: boolean;
  onRelabel?: (id: string, label: string) => void;
  onResizeEnd?: (id: string, w: number, h: number) => void;
  onEditNoteText?: (id: string, text: string) => void;
  onEditLaneLabel?: (id: string, label: string) => void;
  onAnnResizeEnd?: (id: string, w: number, h: number) => void;
  /** ELK-computed positions (unpinned diagrams); wins per-id over the sync layout when present. */
  positionsOverride?: Map<string, Point> | null;
}

/**
 * Estimated tile dimensions from label + shape (mirrors exportSvg's estimator, and the CSS
 * min-sizes). Passed as React Flow `initialWidth/initialHeight` so nodes + edges render
 * immediately — BEFORE live ResizeObserver measurement, and even in environments where RO
 * never fires (the headless-preview failure mode that cost two debugging sessions). Live
 * measurement takes over whenever RO is alive.
 */
/** SSR-style handle anchors matching MermaidNode's target/source sides for the direction. */
function initialHandles(
  direction: string,
  w: number,
  h: number,
): Array<{ type: 'source' | 'target'; position: Position; x: number; y: number; width: number; height: number }> {
  const mk = (type: 'source' | 'target', position: Position, x: number, y: number) => ({
    type,
    position,
    x,
    y,
    width: 1,
    height: 1,
  });
  switch (direction) {
    case 'LR':
      return [mk('target', Position.Left, 0, h / 2), mk('source', Position.Right, w, h / 2)];
    case 'RL':
      return [mk('target', Position.Right, w, h / 2), mk('source', Position.Left, 0, h / 2)];
    case 'BT':
      return [mk('target', Position.Bottom, w / 2, h), mk('source', Position.Top, w / 2, 0)];
    default: // TB / TD
      return [mk('target', Position.Top, w / 2, 0), mk('source', Position.Bottom, w / 2, h)];
  }
}

/** Build React Flow nodes + edges + a frame height + a safe initial viewport from a model. */
function buildFlow(model: DiagramModel, opts: BuildOpts): {
  nodes: AnyFlowNode[];
  edges: Edge[];
  height: number;
  viewport: { x: number; y: number; zoom: number };
} {
  // Sync layered layout is the immediate/fallback layout; ELK positions (async) win per-id.
  const auto = computeLayout(model);
  const positions = opts.positionsOverride
    ? new Map<string, Point>([...auto, ...opts.positionsOverride])
    : auto;
  const sizes = model.sidecar?.size ?? {};
  const laneList = model.sidecar?.lanes ?? [];
  const noteList = model.sidecar?.notes ?? [];

  // Swimlanes render BEHIND the graph (zIndex 0); tiles + stickies sit above (zIndex 1). Both
  // annotation kinds are non-graph: no handles, not connectable, positioned straight from the
  // sidecar (they carry their own pos/size, independent of the auto-layout).
  const laneNodes: LaneFlowNode[] = laneList.map((lane) => ({
    id: lane.id,
    type: 'lane',
    position: { x: lane.pos[0], y: lane.pos[1] },
    data: { lane, onEditLabel: opts.onEditLaneLabel, onResizeEnd: opts.onAnnResizeEnd },
    style: { width: lane.size[0], height: lane.size[1] },
    zIndex: 0,
    draggable: opts.interactive,
    selectable: opts.interactive,
    connectable: false,
  }));

  const graphNodes: MermaidFlowNode[] = [...model.nodes.values()].map((node) => {
    const p = positions.get(node.id) ?? [0, 0];
    const size = sizes[node.id];
    const [estW, estH] = estimateNodeSize(node.label, node.shape);
    const initW = size ? size[0] : estW;
    const initH = size ? size[1] : estH;
    return {
      id: node.id,
      type: 'mermaid',
      position: { x: p[0], y: p[1] },
      data: {
        node,
        direction: model.direction,
        mermaidStyle: effectiveNodeStyle(model, node),
        onRelabel: opts.onRelabel,
        onResizeEnd: opts.onResizeEnd,
        // Fallback dims for FloatingEdge when ResizeObserver hasn't measured yet — keeps edges
        // anchored to the real border even on the no-RO path (matches initialWidth/Height).
        estW: initW,
        estH: initH,
      },
      // Initial dims + handle anchors → nodes AND edges render before (or without)
      // ResizeObserver measurement (React Flow's SSR path). Live measurement refines both.
      initialWidth: initW,
      initialHeight: initH,
      handles: initialHandles(model.direction, initW, initH),
      // Explicit size so the rendered DOM tile matches the size ELK laid out with — render ==
      // layout. Without it the tile fell back to its CSS min (e.g. a diamond clamped to 88px and
      // broke words mid-line), and ELK's spacing/edge-label reservation didn't match the render.
      // A prior NodeResizer drag (sidecar `size`) flows through initW/initH too.
      style: { width: initW, height: initH },
      zIndex: 1,
      draggable: opts.interactive,
      connectable: opts.interactive,
      selectable: opts.interactive,
    };
  });

  const stickyNodes: StickyFlowNode[] = noteList.map((note) => {
    const size = note.size ?? STICKY_DEFAULT_SIZE;
    return {
      id: note.id,
      type: 'sticky',
      position: { x: note.pos[0], y: note.pos[1] },
      data: { note, onEditText: opts.onEditNoteText, onResizeEnd: opts.onAnnResizeEnd },
      style: { width: size[0], height: size[1] },
      zIndex: 1,
      draggable: opts.interactive,
      selectable: opts.interactive,
      connectable: false,
    };
  });

  // Subgraph frames: draw each mermaid `subgraph` as a labeled container behind the graph, sized
  // to its members' bounding box — so the canvas shows the same grouping the mermaid view does.
  // Purely visual (pointer-events:none via CSS); recomputed from positions on every re-seed.
  const FRAME_PAD = 20;
  const FRAME_HEADER = 22;
  const subgraphFrames: SubgraphFlowNode[] = [];
  model.subgraphs.forEach((sg, i) => {
    const members = sg.members.filter((id) => positions.has(id));
    if (members.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const id of members) {
      const [x, y] = positions.get(id) ?? [0, 0];
      const node = model.nodes.get(id);
      const [w, h] = sizes[id] ?? (node ? estimateNodeSize(node.label, node.shape) : [96, 40]);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    }
    subgraphFrames.push({
      id: `sg:${sg.id}`,
      type: 'subgraph',
      position: { x: minX - FRAME_PAD, y: minY - FRAME_PAD - FRAME_HEADER },
      data: { title: sg.title ?? sg.id, accent: PALETTE[i % PALETTE.length].hex },
      style: { width: maxX - minX + FRAME_PAD * 2, height: maxY - minY + FRAME_PAD * 2 + FRAME_HEADER },
      zIndex: 0,
      draggable: false,
      selectable: false,
      connectable: false,
    });
  });

  const nodes: AnyFlowNode[] = [...subgraphFrames, ...laneNodes, ...graphNodes, ...stickyNodes];

  // Floating edges: the path is computed from live node geometry (see FloatingEdge), so arrows
  // terminate on the shape border facing the other tile instead of at a fixed handle.
  const edges: Edge[] = model.edges.map((e) => {
    const marker = edgeMarker(e);
    return {
      id: e.key,
      source: e.from,
      target: e.to,
      type: 'floating',
      label: e.label,
      style: edgeStyle(e),
      markerEnd: marker,
      markerStart: e.arrow === 'bi' ? marker : undefined,
    };
  });

  // Extents include graph positions AND annotation footprints so a tall lane / far sticky isn't
  // stranded off-frame under an identity viewport (the no-ResizeObserver fallback path).
  const xs: number[] = [];
  const ys: number[] = [];
  for (const p of positions.values()) {
    xs.push(p[0]);
    ys.push(p[1]);
  }
  for (const l of laneList) {
    xs.push(l.pos[0]);
    ys.push(l.pos[1], l.pos[1] + l.size[1]);
  }
  for (const n of noteList) {
    xs.push(n.pos[0]);
    ys.push(n.pos[1], n.pos[1] + (n.size ?? STICKY_DEFAULT_SIZE)[1]);
  }
  const viewport = {
    x: 48 - (xs.length ? Math.min(...xs) : 0),
    y: 40 - (ys.length ? Math.min(...ys) : 0),
    zoom: 1,
  };
  return { nodes, edges, height: frameHeight(ys), viewport };
}

/** Provider wrapper — `useReactFlow` (viewport math for click-to-add) needs the context. */
export function DiagramCanvas(props: DiagramCanvasProps): JSX.Element {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

function CanvasInner({
  model,
  readOnly = true,
  onCommit,
  fill = false,
  onMaximize,
  hideChrome = false,
}: DiagramCanvasProps): JSX.Element {
  const interactive = !readOnly && typeof onCommit === 'function';
  const wrapRef = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  const [showMiniMap, setShowMiniMap] = useState(true);
  const [snap, setSnap] = useState(true);
  const [shapeMenuOpen, setShapeMenuOpen] = useState(false);
  const [selNodes, setSelNodes] = useState<string[]>([]);
  const [selEdges, setSelEdges] = useState<string[]>([]);
  const [selStickies, setSelStickies] = useState<string[]>([]);
  const [selLanes, setSelLanes] = useState<string[]>([]);
  const [edgeLabelDraft, setEdgeLabelDraft] = useState('');

  // ── ELK auto-layout (professional layered layout for unpinned diagrams) ──────
  // A diagram with hand-tuned positions keeps them; a fresh/machine one gets ELK. ELK is async,
  // so the first paint uses the sync layered layout (also the no-ResizeObserver path) and ELK
  // positions swap in when they resolve. A token guards against a stale async result overwriting
  // a newer model.
  const isPinned = !!model.sidecar && Object.keys(model.sidecar.pos ?? {}).length > 0;
  const [elkPos, setElkPos] = useState<Map<string, Point> | null>(null);
  const elkToken = useRef(0);
  useEffect(() => {
    const token = (elkToken.current += 1);
    if (isPinned || model.nodes.size === 0) {
      setElkPos(null);
      return;
    }
    const sizeOf = (id: string, label: string, shape: NodeShape): Point =>
      model.sidecar?.size?.[id] ?? estimateNodeSize(label, shape);
    void elkLayout(model, sizeOf).then((pos) => {
      if (elkToken.current === token) setElkPos(pos);
    });
  }, [model, isPinned]);

  // ── commit helpers ─────────────────────────────────────────────────────────

  const commit = useCallback(
    (next: DiagramModel) => {
      if (onCommit) onCommit(serialize(next));
    },
    [onCommit],
  );

  /** Commit a model with its sidecar reconciled (prune geometry for removed nodes/edges). */
  const commitReconciled = useCallback(
    (next: DiagramModel) => {
      if (!onCommit) return;
      onCommit(serialize(applySidecar(next, reconcileAgainstModel(next.sidecar, next))));
    },
    [onCommit],
  );

  // ── layout capture (positions + annotation geometry) ───────────────────────

  /**
   * Snapshot the live canvas geometry into a sidecar patch: graph tiles → `pos`; sticky/lane
   * annotations → their full arrays (pos from the live node, size from live measurement). One
   * optional override lets a resize hand in the authoritative new size for the node being sized.
   */
  const buildLayoutPatch = useCallback(
    (over?: { annId?: string; annSize?: Point; sizeNodeId?: string; sizeVal?: Point }): SidecarData => {
      const patch = emptySidecar();
      const notes: StickyNote[] = [];
      const lanes: Swimlane[] = [];
      for (const n of nodesRef.current) {
        if (n.type === 'sticky') {
          const base = (n.data as StickyNodeData).note;
          notes.push({
            ...cloneNote(base),
            pos: rndPoint(n.position),
            size: over?.annId === n.id && over.annSize ? over.annSize : measuredSize(n, base.size ?? STICKY_DEFAULT_SIZE),
          });
        } else if (n.type === 'lane') {
          const base = (n.data as LaneNodeData).lane;
          lanes.push({
            ...cloneLane(base),
            pos: rndPoint(n.position),
            size: over?.annId === n.id && over.annSize ? over.annSize : measuredSize(n, base.size),
          });
        } else {
          patch.pos[n.id] = rndPoint(n.position);
        }
      }
      patch.notes = notes;
      patch.lanes = lanes;
      if (over?.sizeNodeId && over.sizeVal) patch.size[over.sizeNodeId] = over.sizeVal;
      return patch;
    },
    [],
  );

  /** Merge a sidecar patch onto the model's sidecar and commit the rewritten source. */
  const commitPatch = useCallback(
    (patch: SidecarData) => {
      if (onCommit) onCommit(serialize(applySidecar(model, mergeSidecar(model.sidecar, patch))));
    },
    [model, onCommit],
  );

  // ── inline relabel + resize (into node data) ───────────────────────────────

  const relabel = useCallback(
    (id: string, label: string) => commit(updateNodeLabel(model, id, label)),
    [model, commit],
  );

  const resizeEnd = useCallback(
    (id: string, w: number, h: number) => commitPatch(buildLayoutPatch({ sizeNodeId: id, sizeVal: [w, h] })),
    [commitPatch, buildLayoutPatch],
  );

  // ── sticky / lane text + resize (annotation edits into the sidecar) ────────

  const editNoteText = useCallback(
    (id: string, text: string) => {
      const notes = (model.sidecar?.notes ?? []).map(cloneNote).map((n) => (n.id === id ? { ...n, text } : n));
      commitPatch(annPatch(notes, (model.sidecar?.lanes ?? []).map(cloneLane)));
    },
    [model, commitPatch],
  );
  const editLaneLabel = useCallback(
    (id: string, label: string) => {
      const lanes = (model.sidecar?.lanes ?? []).map(cloneLane).map((l) => (l.id === id ? { ...l, label } : l));
      commitPatch(annPatch((model.sidecar?.notes ?? []).map(cloneNote), lanes));
    },
    [model, commitPatch],
  );
  const annResizeEnd = useCallback(
    (id: string, w: number, h: number) => commitPatch(buildLayoutPatch({ annId: id, annSize: [w, h] })),
    [commitPatch, buildLayoutPatch],
  );

  const built = useMemo(
    () =>
      buildFlow(model, {
        interactive,
        onRelabel: interactive ? relabel : undefined,
        onResizeEnd: interactive ? resizeEnd : undefined,
        onEditNoteText: interactive ? editNoteText : undefined,
        onEditLaneLabel: interactive ? editLaneLabel : undefined,
        onAnnResizeEnd: interactive ? annResizeEnd : undefined,
        positionsOverride: elkPos,
      }),
    [model, interactive, relabel, resizeEnd, editNoteText, editLaneLabel, annResizeEnd, elkPos],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<AnyFlowNode>(built.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(built.edges);

  // Re-seed when the model changes (after a commit rewrote the source, or a hand edit).
  useEffect(() => {
    setNodes(built.nodes);
    setEdges(built.edges);
  }, [built, setNodes, setEdges]);

  // Latest node positions, so drag-stop / resize / add can read every node's final position.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  // Commit the FULL current layout to the sidecar (one `%% @aletheia` line; no reflow on
  // reload). Fired ONLY from real user gestures (drag-stop / arrow keydown) — never from React
  // Flow's internal change stream, which would loop (the Phase 2b lesson). Captures graph tile
  // positions AND sticky/lane geometry in one patch.
  const commitLayout = useCallback(() => {
    if (onCommit) commitPatch(buildLayoutPatch());
  }, [onCommit, commitPatch, buildLayoutPatch]);

  // ── graph mutations ────────────────────────────────────────────────────────

  const onConnect = useCallback(
    (c: Connection) => {
      if (!onCommit || !c.source || !c.target || c.source === c.target) return;
      commit(addEdge(model, c.source, c.target));
    },
    [model, onCommit, commit],
  );

  /** Delete a mix of graph nodes/edges + sticky/lane annotations, then reconcile orphan geometry. */
  const deleteIds = useCallback(
    (nodeIds: string[], edgeKeys: string[], stickyIds: string[], laneIds: string[]) => {
      if (!onCommit) return;
      let m = model;
      for (const id of nodeIds) m = removeNode(m, id);
      for (const key of edgeKeys) m = removeEdge(m, key);
      if (stickyIds.length > 0 || laneIds.length > 0) {
        const notes = (m.sidecar?.notes ?? []).filter((n) => !stickyIds.includes(n.id));
        const lanes = (m.sidecar?.lanes ?? []).filter((l) => !laneIds.includes(l.id));
        m = applySidecar(m, mergeSidecar(m.sidecar, annPatch(notes, lanes)));
      }
      commitReconciled(m);
    },
    [model, onCommit, commitReconciled],
  );

  const onDelete = useCallback(
    ({ nodes: dn, edges: de }: { nodes: RFNode[]; edges: Edge[] }) => {
      deleteIds(
        dn.filter((n) => n.type === 'mermaid').map((n) => n.id),
        de.map((e) => e.id),
        dn.filter((n) => n.type === 'sticky').map((n) => n.id),
        dn.filter((n) => n.type === 'lane').map((n) => n.id),
      );
    },
    [deleteIds],
  );

  const deleteSelection = useCallback(
    () => deleteIds(selNodes, selEdges, selStickies, selLanes),
    [deleteIds, selNodes, selEdges, selStickies, selLanes],
  );

  const onReconnect = useCallback(
    (oldEdge: Edge, conn: Connection) => {
      if (!onCommit || !conn.source || !conn.target) return;
      commitReconciled(reconnectEdge(model, oldEdge.id, conn.source, conn.target));
    },
    [model, onCommit, commitReconciled],
  );

  /** Add a tile of the given shape at the viewport center (small cascade so adds don't stack). */
  const addShape = useCallback(
    (shape: NodeShape) => {
      if (!onCommit || !wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const cascade = (model.nodes.size % 5) * 20;
      const center = screenToFlowPosition({
        x: rect.left + rect.width / 2 + cascade,
        y: rect.top + rect.height / 2 + cascade,
      });
      const id = nextNodeId(model);
      const label = SHAPE_LABELS[shape] ?? 'New';
      let m = addNode(model, { id, label, shape, quoted: false, classes: [] });
      const patch = emptySidecar();
      patch.pos[id] = [Math.round(center.x), Math.round(center.y)];
      m = applySidecar(m, mergeSidecar(m.sidecar, patch));
      commit(m);
    },
    [model, onCommit, commit, screenToFlowPosition],
  );

  /** The flow-coordinate point at the visible canvas center (with a small per-add cascade). */
  const centerFlowPoint = useCallback(
    (cascade: number) => {
      const rect = wrapRef.current!.getBoundingClientRect();
      return screenToFlowPosition({
        x: rect.left + rect.width / 2 + cascade,
        y: rect.top + rect.height / 2 + cascade,
      });
    },
    [screenToFlowPosition],
  );

  /** Add a sticky note at the canvas center. */
  const addSticky = useCallback(() => {
    if (!onCommit || !wrapRef.current) return;
    const existing = model.sidecar?.notes ?? [];
    const c = centerFlowPoint((existing.length % 5) * 18);
    const [w, h] = STICKY_DEFAULT_SIZE;
    const note: StickyNote = {
      id: nextAnnId('s', existing.map((n) => n.id)),
      text: '',
      pos: [Math.round(c.x - w / 2), Math.round(c.y - h / 2)],
      size: STICKY_DEFAULT_SIZE,
      color: STICKY_DEFAULT,
    };
    commitPatch(annPatch([...existing.map(cloneNote), note], (model.sidecar?.lanes ?? []).map(cloneLane)));
  }, [model, onCommit, commitPatch, centerFlowPoint]);

  /** Add a swimlane / frame at the canvas center. */
  const addLane = useCallback(() => {
    if (!onCommit || !wrapRef.current) return;
    const existing = model.sidecar?.lanes ?? [];
    const c = centerFlowPoint((existing.length % 5) * 18);
    const [w, h] = LANE_DEFAULT_SIZE;
    const lane: Swimlane = {
      id: nextAnnId('lane', existing.map((l) => l.id)),
      label: 'Lane',
      pos: [Math.round(c.x - w / 2), Math.round(c.y - h / 2)],
      size: LANE_DEFAULT_SIZE,
      color: LANE_DEFAULT,
      orientation: 'horizontal',
    };
    commitPatch(annPatch((model.sidecar?.notes ?? []).map(cloneNote), [...existing.map(cloneLane), lane]));
  }, [model, onCommit, commitPatch, centerFlowPoint]);

  /** Recolor every selected sticky + lane to a portable accent hex. */
  const applyAnnColor = useCallback(
    (hex: string) => {
      if (!onCommit || (selStickies.length === 0 && selLanes.length === 0)) return;
      const notes = (model.sidecar?.notes ?? [])
        .map(cloneNote)
        .map((n) => (selStickies.includes(n.id) ? { ...n, color: hex } : n));
      const lanes = (model.sidecar?.lanes ?? [])
        .map(cloneLane)
        .map((l) => (selLanes.includes(l.id) ? { ...l, color: hex } : l));
      commitPatch(annPatch(notes, lanes));
    },
    [model, onCommit, commitPatch, selStickies, selLanes],
  );

  /** Apply a quick-style chip (fill+stroke pair) — or clear — to every selected node. */
  const applyChip = useCallback(
    (hex: string | null) => {
      if (!onCommit || selNodes.length === 0) return;
      let m = model;
      for (const id of selNodes) {
        m = setNodeStyle(
          m,
          id,
          hex === null ? { fill: null, stroke: null } : { fill: `${hex}22`, stroke: hex },
        );
      }
      commit(m);
    },
    [model, onCommit, selNodes, commit],
  );

  const applyTextColor = useCallback(
    (hex: string | null) => {
      if (!onCommit || selNodes.length === 0) return;
      let m = model;
      for (const id of selNodes) m = setNodeStyle(m, id, { color: hex });
      commit(m);
    },
    [model, onCommit, selNodes, commit],
  );

  const applyFontSize = useCallback(
    (px: string) => {
      if (!onCommit || selNodes.length === 0) return;
      let m = model;
      for (const id of selNodes) m = setNodeStyle(m, id, { 'font-size': px === '' ? null : px });
      commit(m);
    },
    [model, onCommit, selNodes, commit],
  );

  const applyConnector = useCallback(
    (connector: ConnectorStyle) => {
      if (!onCommit || selEdges.length === 0) return;
      let m = model;
      for (const key of selEdges) m = updateEdge(m, key, { connector });
      commit(m);
    },
    [model, onCommit, selEdges, commit],
  );

  const applyArrow = useCallback(
    (arrow: 'arrow' | 'open') => {
      if (!onCommit || selEdges.length === 0) return;
      let m = model;
      for (const key of selEdges) m = updateEdge(m, key, { arrow });
      commit(m);
    },
    [model, onCommit, selEdges, commit],
  );

  const applyEdgeLabel = useCallback(() => {
    if (!onCommit || selEdges.length !== 1) return;
    commit(updateEdge(model, selEdges[0], { label: edgeLabelDraft === '' ? null : edgeLabelDraft }));
  }, [model, onCommit, selEdges, edgeLabelDraft, commit]);

  /** Drop all saved positions → the deterministic auto-layout takes over (sizes/styles kept). */
  const relayout = useCallback(() => {
    if (!onCommit) return;
    const sc = model.sidecar ? cloneSidecar(model.sidecar) : emptySidecar();
    sc.pos = {};
    onCommit(serialize(applySidecar(model, sc)));
  }, [model, onCommit]);

  // ── selection tracking ─────────────────────────────────────────────────────

  const onSelectionChange = useCallback(
    ({ nodes: sn, edges: se }: { nodes: RFNode[]; edges: Edge[] }) => {
      setSelNodes(sn.filter((n) => n.type === 'mermaid').map((n) => n.id));
      setSelStickies(sn.filter((n) => n.type === 'sticky').map((n) => n.id));
      setSelLanes(sn.filter((n) => n.type === 'lane').map((n) => n.id));
      setSelEdges(se.map((e) => e.id));
      if (se.length === 1) {
        const label = model.edges.find((e) => e.key === se[0].id)?.label ?? '';
        setEdgeLabelDraft(label);
      }
    },
    [model],
  );

  // Keyboard-move commit: gated on a real arrow keydown (debounced) — the change stream can't loop.
  const commitTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
    },
    [],
  );
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!interactive || !e.key.startsWith('Arrow')) return;
      if (commitTimer.current !== null) window.clearTimeout(commitTimer.current);
      commitTimer.current = window.setTimeout(() => {
        commitTimer.current = null;
        commitLayout();
      }, 220);
    },
    [interactive, commitLayout],
  );

  const hasAnnSelection = selStickies.length > 0 || selLanes.length > 0;
  const hasSelection = selNodes.length > 0 || selEdges.length > 0 || hasAnnSelection;

  return (
    <div
      ref={wrapRef}
      className={`diagram-canvas${fill ? ' diagram-canvas--fill' : ''}`}
      style={{ height: fill ? '100%' : built.height }}
      data-testid="diagram-canvas"
      onKeyDown={onKeyDown}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        fitView
        defaultViewport={built.viewport}
        onNodesChange={interactive ? onNodesChange : undefined}
        onEdgesChange={interactive ? onEdgesChange : undefined}
        onNodeDragStop={interactive ? commitLayout : undefined}
        onConnect={interactive ? onConnect : undefined}
        onDelete={interactive ? onDelete : undefined}
        onReconnect={interactive ? onReconnect : undefined}
        onSelectionChange={interactive ? onSelectionChange : undefined}
        deleteKeyCode={interactive ? ['Delete', 'Backspace'] : null}
        nodesDraggable={interactive}
        nodesConnectable={interactive}
        elementsSelectable={interactive}
        // Swimlanes stay behind the graph even when selected (they're frames, not focus targets).
        elevateNodesOnSelect={false}
        // Lucid-style pointer model: left-drag on the pane box-selects; middle/right-drag pans.
        selectionOnDrag={interactive}
        selectionMode={SelectionMode.Partial}
        panOnDrag={interactive ? [1, 2] : true}
        onPaneContextMenu={(e) => e.preventDefault()}
        // Loose: a connection may start or end on any handle — tiles feel Lucid-like.
        connectionMode={ConnectionMode.Loose}
        connectionRadius={28}
        snapToGrid={interactive && snap}
        snapGrid={SNAP}
        // Embedded in a scrollable editor: allow pan + controls zoom, never hijack wheel-scroll.
        zoomOnScroll={false}
        panOnScroll={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: false }}
      >
        {!hideChrome && <Background variant={BackgroundVariant.Dots} gap={16} size={1} />}
        {!hideChrome && (
          <Controls showInteractive={false}>
            {onMaximize && (
              <ControlButton
                onClick={onMaximize}
                title="Maximize (fullscreen)"
                aria-label="Maximize diagram"
                data-testid="dg-maximize"
              >
                <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"
                  />
                </svg>
              </ControlButton>
            )}
          </Controls>
        )}
        {interactive && showMiniMap && !hideChrome && (
          <MiniMap
            pannable
            zoomable
            className="diagram-minimap"
            nodeColor={(n) =>
              ((n as MermaidFlowNode).data?.mermaidStyle?.fill as string) ?? 'var(--dg-mini-node)'
            }
          />
        )}

        {interactive && !hideChrome && (
          <Panel position="top-left" className="dg-toolbar" data-testid="dg-toolbar">
            <button
              type="button"
              className="dg-btn dg-shape-btn"
              data-active={shapeMenuOpen}
              title="Add a shape"
              onClick={() => setShapeMenuOpen((v) => !v)}
            >
              <ShapeGlyph shape="rectangle" />
              <span style={{ fontSize: 11 }}>Shapes ▾</span>
            </button>
            {shapeMenuOpen && (
              <div className="dg-shape-flyout" data-testid="dg-shape-flyout" onMouseLeave={() => setShapeMenuOpen(false)}>
                {SHAPE_MENU.map((group) => (
                  <div key={group.group}>
                    <div className="dg-shape-group">{group.group}</div>
                    <div className="dg-shape-grid">
                      {group.items.map((it) => (
                        <button
                          key={it.shape}
                          type="button"
                          className="dg-shape-item"
                          title={`Add ${it.label}`}
                          onClick={() => {
                            addShape(it.shape);
                            setShapeMenuOpen(false);
                          }}
                        >
                          <ShapeGlyph shape={it.shape} />
                          <span>{it.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <span className="dg-sep" />
            <button
              type="button"
              className="dg-btn dg-shape-btn"
              title="Add a sticky note"
              data-testid="dg-add-sticky"
              onClick={addSticky}
            >
              <span style={{ fontSize: 13 }}>🗒</span>
              <span style={{ fontSize: 11 }}>Note</span>
            </button>
            <button
              type="button"
              className="dg-btn dg-shape-btn"
              title="Add a swimlane / frame"
              data-testid="dg-add-lane"
              onClick={addLane}
            >
              <span style={{ fontSize: 13 }}>▭</span>
              <span style={{ fontSize: 11 }}>Lane</span>
            </button>
            <span className="dg-sep" />
            <button type="button" className="dg-btn" title="Auto-layout (drop saved positions)" onClick={relayout}>
              ⟲
            </button>
            <button
              type="button"
              className="dg-btn"
              data-active={snap}
              title="Snap to grid"
              onClick={() => setSnap((v) => !v)}
            >
              ⌗
            </button>
            <button
              type="button"
              className="dg-btn"
              data-active={showMiniMap}
              title="Minimap"
              onClick={() => setShowMiniMap((v) => !v)}
            >
              ▦
            </button>
          </Panel>
        )}

        {interactive && hasSelection && !hideChrome && (
          <Panel position="top-right" className="dg-toolbar dg-selbar" data-testid="dg-selbar">
            {selNodes.length > 0 && (
              <>
                <span className="dg-label">Fill</span>
                <button type="button" className="dg-swatch dg-swatch-none" title="Clear style" onClick={() => applyChip(null)} />
                {PALETTE.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    className="dg-swatch"
                    style={{ background: `${c.hex}33`, borderColor: c.hex }}
                    title={c.name}
                    onClick={() => applyChip(c.hex)}
                  />
                ))}
                <span className="dg-sep" />
                <span className="dg-label">Text</span>
                <button type="button" className="dg-swatch dg-swatch-none" title="Theme text color" onClick={() => applyTextColor(null)} />
                {PALETTE.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    className="dg-swatch dg-swatch-text"
                    style={{ color: c.hex }}
                    title={`text ${c.name}`}
                    onClick={() => applyTextColor(c.hex)}
                  >
                    A
                  </button>
                ))}
                <select
                  className="dg-select"
                  title="Text size"
                  defaultValue=""
                  onChange={(e) => applyFontSize(e.target.value)}
                >
                  {FONT_SIZES.map((f) => (
                    <option key={f.label} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </>
            )}
            {selEdges.length > 0 && (
              <>
                <span className="dg-label">Line</span>
                <button type="button" className="dg-btn" title="Solid arrow" onClick={() => { applyConnector('solid'); applyArrow('arrow'); }}>
                  →
                </button>
                <button type="button" className="dg-btn" title="Dotted" onClick={() => applyConnector('dotted')}>
                  ⇢
                </button>
                <button type="button" className="dg-btn" title="Thick" onClick={() => applyConnector('thick')}>
                  ⇒
                </button>
                <button type="button" className="dg-btn" title="No arrowhead" onClick={() => applyArrow('open')}>
                  —
                </button>
                {selEdges.length === 1 && (
                  <input
                    className="dg-input"
                    placeholder="label"
                    value={edgeLabelDraft}
                    onChange={(e) => setEdgeLabelDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyEdgeLabel();
                      e.stopPropagation();
                    }}
                    onBlur={applyEdgeLabel}
                  />
                )}
              </>
            )}
            {hasAnnSelection && (
              <>
                <span className="dg-label">{selLanes.length > 0 && selStickies.length === 0 ? 'Lane' : 'Note'}</span>
                {PALETTE.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    className="dg-swatch"
                    style={{ background: `${c.hex}33`, borderColor: c.hex }}
                    title={c.name}
                    onClick={() => applyAnnColor(c.hex)}
                  />
                ))}
              </>
            )}
            <span className="dg-sep" />
            <button type="button" className="dg-btn dg-btn--danger" title="Delete selection" onClick={deleteSelection}>
              🗑
            </button>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
}
